import React, { useState } from 'react';
import { X, Download, Database, Filter, CheckCircle } from 'lucide-react';
import { exportLeads } from '../services/exportService';
import { showToast } from '@/components/ui/Toast';

interface ExportModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedRows: string[];
    totalFilteredCount?: number;
    currentFilters?: any;
}

const ExportModal: React.FC<ExportModalProps> = ({
    isOpen,
    onClose,
    selectedRows,
    totalFilteredCount = 0,
    currentFilters = {}
}) => {
    const [exportType, setExportType] = useState<'all' | 'filtered' | 'selected'>('filtered');
    const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('csv');
    const [includeUTMData, setIncludeUTMData] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
    const [exportComplete, setExportComplete] = useState(false);

    const handleExport = async () => {
        setIsExporting(true);

        try {
            let exportOptions;

            switch (exportType) {
                case 'all':
                    exportOptions = {
                        export_data: true,
                        include_utm_data: includeUTMData,
                        export_all: true,
                        primary_filters: {},
                        custom_filters: {},
                        format: exportFormat
                    };
                    break;

                case 'filtered':
                    exportOptions = {
                        export_data: true,
                        include_utm_data: includeUTMData,
                        export_all: true,
                        primary_filters: currentFilters.primary_filters || {},
                        custom_filters: currentFilters.custom_filters || {},
                        format: exportFormat
                    };
                    break;

                case 'selected':
                    if (selectedRows.length === 0) {
                        showToast('Please select at least one lead to export', 'error');
                        return;
                    }
                    exportOptions = {
                        export_data: true,
                        include_utm_data: includeUTMData,
                        selected_lead_ids: selectedRows,
                        export_all: false,
                        format: exportFormat
                    };
                    break;
            }

            const url = await exportLeads(exportOptions);

            // Set the download URL and mark export as complete
            setDownloadUrl(url);
            setExportComplete(true);
            showToast('✅ Export completed! Click the download button to get your file.', 'success');

        } catch (error) {
            console.error('Export failed:', error);
            showToast(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const handleDownload = () => {
        if (downloadUrl) {
            window.open(downloadUrl, '_blank');
        }
    };

    const handleClose = () => {
        // Reset state when closing
        setDownloadUrl(null);
        setExportComplete(false);
        setExportType('filtered');
        setExportFormat('csv');
        setIncludeUTMData(false);
        onClose();
    };

    const MAX_EXPORT_LIMIT = 10000;

    const getExportCount = () => {
        switch (exportType) {
            case 'all':
                return 'Up to 10,000 leads';
            case 'filtered':
                if (totalFilteredCount > MAX_EXPORT_LIMIT) {
                    return `${MAX_EXPORT_LIMIT.toLocaleString()} of ${totalFilteredCount.toLocaleString()} filtered leads`;
                }
                return `${totalFilteredCount.toLocaleString()} filtered leads`;
            case 'selected':
                return `${selectedRows.length} selected leads`;
            default:
                return '0 leads';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <Download className="w-4 h-4 text-green-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Export Leads</h3>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6 overflow-y-auto flex-1">
                    {/* Show download button if export is complete */}
                    {exportComplete && downloadUrl && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-green-900">Export Complete!</div>
                                        <div className="text-xs text-green-700">Your file is ready to download</div>
                                    </div>
                                </div>
                                <button
                                    onClick={handleDownload}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                                >
                                    <Download className="w-4 h-4" />
                                    Download
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Export Type Selection - Hide when export is complete */}
                    {!exportComplete && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            What would you like to export?
                        </label>
                        <div className="space-y-3">
                            {/* All Leads */}
                            <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                <input
                                    type="radio"
                                    name="exportType"
                                    value="all"
                                    checked={exportType === 'all'}
                                    onChange={(e) => setExportType(e.target.value as any)}
                                    className="text-brandColor focus:ring-brandColor"
                                />
                                <div className="ml-3 flex items-center">
                                    <Database className="w-4 h-4 text-gray-500 mr-2" />
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">All Leads</div>
                                        <div className="text-xs text-gray-500">Export up to 10,000 leads from the database</div>
                                    </div>
                                </div>
                            </label>

                            {/* Filtered Leads */}
                            <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                <input
                                    type="radio"
                                    name="exportType"
                                    value="filtered"
                                    checked={exportType === 'filtered'}
                                    onChange={(e) => setExportType(e.target.value as any)}
                                    className="text-brandColor focus:ring-brandColor"
                                />
                                <div className="ml-3 flex items-center">
                                    <Filter className="w-4 h-4 text-gray-500 mr-2" />
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">Current Filter Results</div>
                                        <div className="text-xs text-gray-500">
                                            {totalFilteredCount > MAX_EXPORT_LIMIT
                                                ? `Export ${MAX_EXPORT_LIMIT.toLocaleString()} of ${totalFilteredCount.toLocaleString()} leads matching current filters`
                                                : `Export ${totalFilteredCount.toLocaleString()} leads matching current filters`
                                            }
                                        </div>
                                    </div>
                                </div>
                            </label>

                            {/* Selected Leads */}
                            <label className={`flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                                selectedRows.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                            }`}>
                                <input
                                    type="radio"
                                    name="exportType"
                                    value="selected"
                                    checked={exportType === 'selected'}
                                    onChange={(e) => setExportType(e.target.value as any)}
                                    disabled={selectedRows.length === 0}
                                    className="text-brandColor focus:ring-brandColor"
                                />
                                <div className="ml-3 flex items-center">
                                    <CheckCircle className="w-4 h-4 text-gray-500 mr-2" />
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">Selected Leads</div>
                                        <div className="text-xs text-gray-500">
                                            Export {selectedRows.length} manually selected leads
                                            {selectedRows.length === 0 && ' (No leads selected)'}
                                        </div>
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>
                    )}

                    {/* Options - Hide when export is complete */}
                    {!exportComplete && (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                Export Format
                            </label>
                            <div className="flex gap-3">
                                <label className="flex-1 flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                    <input
                                        type="radio"
                                        name="exportFormat"
                                        value="csv"
                                        checked={exportFormat === 'csv'}
                                        onChange={(e) => setExportFormat(e.target.value as 'csv' | 'excel')}
                                        className="text-brandColor focus:ring-brandColor"
                                    />
                                    <div className="ml-3">
                                        <div className="text-sm font-medium text-gray-900">CSV</div>
                                        <div className="text-xs text-gray-500">Comma-separated values</div>
                                    </div>
                                </label>
                                <label className="flex-1 flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                    <input
                                        type="radio"
                                        name="exportFormat"
                                        value="excel"
                                        checked={exportFormat === 'excel'}
                                        onChange={(e) => setExportFormat(e.target.value as 'csv' | 'excel')}
                                        className="text-brandColor focus:ring-brandColor"
                                    />
                                    <div className="ml-3">
                                        <div className="text-sm font-medium text-gray-900">Excel</div>
                                        <div className="text-xs text-gray-500">.xlsx format</div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                Additional Options
                            </label>
                            <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                <input
                                    type="checkbox"
                                    checked={includeUTMData}
                                    onChange={(e) => setIncludeUTMData(e.target.checked)}
                                    className="text-brandColor focus:ring-brandColor"
                                />
                                <div className="ml-3">
                                    <div className="text-sm font-medium text-gray-900">Include Tracking Data</div>
                                    <div className="text-xs text-gray-500">
                                        Include UTM parameters, Facebook, and LinkedIn tracking data
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>
                    )}

                    {/* Summary - Hide when export is complete */}
                    {!exportComplete && (
                    <div className="bg-brandColor/10 border border-brandColor/30 rounded-lg p-4">
                        <div className="text-sm">
                            <span className="font-medium text-brandColor">Ready to export: </span>
                            <span className="text-brandColor">{getExportCount()}</span>
                        </div>
                        <div className="text-xs text-brandColor mt-1">
                            Format: {exportFormat.toUpperCase()}
                            {includeUTMData && ' + Tracking data'}
                        </div>
                        <div className="text-xs text-orange-700 mt-2 pt-2 border-t border-brandColor/30">
                            ⚠️ Maximum 10,000 leads per export
                        </div>
                        <div className="text-xs text-green-700 mt-1">
                            💾 Your file will be ready to download when complete
                        </div>
                    </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                    >
                        {exportComplete ? 'Close' : 'Cancel'}
                    </button>
                    {!exportComplete && (
                        <button
                            onClick={handleExport}
                            disabled={isExporting || (exportType === 'selected' && selectedRows.length === 0)}
                            className="px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            {isExporting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <Download className="w-4 h-4" />
                                    Export
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ExportModal;